import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getObjectId, Id, SavedLocation } from '@hero/schema';
import { ModalForm } from '../../../app/Modal';
import { LocationForm } from '../Form/SavedLocationForm';
import { useSavedLocationCreateAdapter } from '@hero/web-hooks';
import { isValidCoords } from '@hero/core';

export interface LocationEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLocationSaved: (location: SavedLocation) => void;
  mode?: 'create' | 'edit';
  initialCoordinates?: { lat: number; lng: number };
  /**
   * Google Maps link the pin already came from (an order stop whose accuracy
   * was confirmed via a pasted link). Pre-filled so the same provenance
   * lands on `SavedLocation.locationUrl` without the user pasting it twice.
   */
  initialLocationUrl?: string;
  savedLocationId?: Id;
  /**
   * Pre-fill the client field when creating a location for a specific
   * Client. Pair with {@link forceType}=`'client'` to keep the
   * selectors locked (used by Client CRM screens).
   */
  clientId?: Id;
  /**
   * Lock the location type to `'own'` or `'client'`. When set, the
   * type selector is hidden; for `'client'` the client selector is
   * also hidden and replaced by a {@link ClientLinkingBanner} that
   * surfaces the locked client.
   */
  forceType?: 'own' | 'client';
  allowOutsideClickClose?: boolean;
  orgId?: string;
}

export function LocationEditModal({
  isOpen,
  onClose,
  onLocationSaved,
  mode = 'create',
  /** When false, modal only closes via Close/X or Save/Cancel (recommended when modal contains nested create modals). ModalForm already supports this. */
  allowOutsideClickClose = false,
  savedLocationId,
  initialCoordinates,
  initialLocationUrl,
  clientId,
  forceType,
}: LocationEditModalProps) {
  const { t } = useTranslation(['m-location', 'c-main']);

  const isNewLocation = !savedLocationId;
  const forceClientId = forceType === 'client' && clientId ? getObjectId(clientId) : undefined;
  /** Only pass initial coords in create mode; edit mode must never receive map coords so the loaded entity is the single source of truth. */
  const { createLocation, updateLocation, formHook, savedLocation, reset, onSaveClick } =
    useSavedLocationCreateAdapter({
      onLocationSaved,
      savedLocationId,
      forceType,
      forceClientId,
      defaultValues: isNewLocation
        ? {
            location: initialCoordinates,
            locationUrl: initialLocationUrl,
            clientId: clientId ? getObjectId(clientId) : undefined,
          }
        : undefined,
    });

  const handleClose = () => {
    reset();
    onClose();
  };

  // When opening for create with valid map coords, sync form (defaultValues only apply on first mount)
  useEffect(() => {
    if (isOpen && isNewLocation && isValidCoords(initialCoordinates)) {
      formHook.setValues('location', initialCoordinates);
    }
  }, [isOpen, isNewLocation, initialCoordinates, formHook.setValues]);

  // Same reason as the coords sync above: the modal instance outlives a
  // single open, so `defaultValues` alone would keep the first stop's link.
  useEffect(() => {
    if (isOpen && isNewLocation && initialLocationUrl) {
      formHook.setValues('locationUrl', initialLocationUrl);
    }
  }, [isOpen, isNewLocation, initialLocationUrl, formHook.setValues]);

  const currentLoading =
    savedLocation.isLoading || createLocation.isPending || updateLocation.isPending;

  const currentSuccess = createLocation.isSuccess || updateLocation.isSuccess;

  return (
    <ModalForm
      isOpen={isOpen}
      onClose={handleClose}
      onSave={onSaveClick}
      title={mode === 'edit' ? t('modal.edit_location_title') : t('modal.save_location_title')}
      description={
        mode === 'edit'
          ? t('modal.edit_location_description')
          : t('modal.save_location_description')
      }
      isLoading={currentLoading}
      isSuccess={currentSuccess}
      allowOutsideClickClose={allowOutsideClickClose}
      size="2xl"
      saveText={mode === 'edit' ? t('c-main:crud_buttons.update') : t('c-main:crud_buttons.save')}
      disableSave={!formHook.isFormValid() || formHook.isLocationEditing}>
      <LocationForm
        formHook={formHook}
        isLoading={currentLoading}
        hideSubmitButton={true}
        initialAddress={savedLocation?.data?.address}
      />
    </ModalForm>
  );
}
